import { useEffect, useRef, useState } from "react";

import { API_CONFIG } from "@/infrastructure/config/api";
import { logger } from "@/infrastructure/lib/logger";
import { activeOrganizationHeader } from "@/shared/utils/fetchApi";
import { createAuthenticatedSSE, type SSESubscription } from "@/shared/utils/fetchSSE";

import { reconnectDelay } from "../utils/runs";

export interface JobRunChange {
  execution_id: string;
  job_id: string;
  status: string;
}

/**
 * As mudanças de status das execuções dos jobs do workspace, em SSE
 * (AB#86605, task 86870). O engine avisa o api, e o api repassa aqui.
 *
 * `connected` diz se o aviso está chegando: enquanto não está, a tela segue
 * pela consulta periódica, que fica como reserva. A conexão reabre sozinha —
 * com espera crescente se cair, e na hora quando a aba volta a ficar visível,
 * o que também pega um job criado depois de a conexão abrir.
 */
export function useJobRunsEvents(productId: string | null | undefined, onChange: (change: JobRunChange) => void) {
  const [connected, setConnected] = useState(false);
  const handler = useRef(onChange);
  handler.current = onChange;

  useEffect(() => {
    if (!productId) return;
    let subscription: SSESubscription | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      subscription?.close();
      const url = `${API_CONFIG.API_ROOT_URL}/jobs/runs/events?${new URLSearchParams({ product_id: productId })}`;
      subscription = createAuthenticatedSSE(url, {
        headers: activeOrganizationHeader(),
        onOpen: () => {
          attempt = 0;
          setConnected(true);
        },
        onMessage: (data, eventType) => {
          if (eventType !== "run-changed") return;
          try {
            handler.current(JSON.parse(data) as JobRunChange);
          } catch (error) {
            logger.warn("[useJobRunsEvents] Ignoring a malformed run-changed event", { error });
          }
        },
        onError: (error) => {
          logger.warn("[useJobRunsEvents] Run events stream dropped; polling until it is back", {
            error: error.message,
          });
          retry();
        },
        onClose: retry,
      });
    };

    function retry() {
      setConnected(false);
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(connect, reconnectDelay(attempt++));
    }

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      attempt = 0;
      clearTimeout(timer);
      connect();
    };

    connect();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      subscription?.close();
      setConnected(false);
    };
  }, [productId]);

  return { connected };
}
